// The player showcase popup and its hover preview. Any trainer name drawn with
// playerLink (chat, Ranks, guild lists, records, friends, the market) opens
// the popup on click; a mouse resting on one shows a small preview. The
// popup is a layer over the page, not a dialog: the hunt keeps running and
// Esc, the close button or a click outside closes it. A modified click
// (new tab) still follows the link to the full #/player page.
import type { TierId } from '../shared/arena.ts';
import { BADGE } from '../shared/pass.ts';
import { SPECIES } from '../shared/species.ts';
import { trainerXpToNext } from '../shared/xp.ts';
import { SOCIAL, type ProfileDTO } from '../shared/social.ts';
import type { CreatureDTO } from '../shared/protocol.ts';
import { F, askProfile, bindCard, actionsHtml } from './social.ts';
import { playerIcon } from './playericon.ts';
import { creatureIcon } from './assets.ts';
import { creatureArt } from './skins.ts';
import { esc, full, num, cname, elChip } from './fmt.ts';
import { creatureGrade } from './badges.ts';
import { rollPills } from './rollpills.ts';
import { tierBadge, teamFace } from './arena.ts';
import { arenaOpensAt, leagueBadge } from './ranked.ts';
import type { LeagueId } from '../shared/ranked.ts';
import { tagChip } from './guild.ts';
import { setSnapshot } from './hovercard.ts';
import { placeBeside } from './tip.ts';
import { phoneSections, psecClick } from './psec.ts';
import { fitLists, setFitPage } from './fitlist.ts';

const lc = (s: string) => s.toLowerCase();
/** A profile this fresh is shown from memory on hover without asking again. */
const PEEK_FRESH_MS = 60_000;
const HOVER_DELAY_MS = 380;

let layer: HTMLElement | null = null;
let tip: HTMLElement | null = null;
let hoverName = '';
let hoverEl: HTMLElement | null = null;
let hoverTimer = 0;

const nameOf = (el: HTMLElement) => el.dataset.player ?? '';

/** The companion as the icon shows it. */
const compOf = (p: ProfileDTO) => (p.companion ? { species: p.companion.species, aura: p.companion.rolls.aura } : null);

export function openProfileCard(name: string) {
  closeTip();
  F.popup = lc(name);
  askProfile(name);
  draw();
}

export function closeProfileCard() {
  F.popup = '';
  layer?.remove();
  layer = null;
}

export const profileCardOpen = () => !!F.popup;

function draw() {
  if (!F.popup) return;
  if (!layer) {
    layer = document.createElement('div');
    layer.className = 'ppop-layer';
    layer.addEventListener('click', (e) => {
      const t = e.target as HTMLElement;
      if (t === layer || t.closest('[data-ppop-close]')) return closeProfileCard();
      // Its own tabs and pager: redraw the popup, not the page under it.
      const tab = t.closest<HTMLElement>('[data-act="psec"]');
      if (tab?.dataset.id?.startsWith('ppx:')) { e.stopPropagation(); psecClick('psec', tab.dataset.id); draw(); return; }
      const go = t.closest<HTMLElement>('[data-fit-go]');
      if (go?.dataset.fitGo?.startsWith('ppx-')) { e.stopPropagation(); const [id, n] = go.dataset.fitGo.split(':'); setFitPage(id, Number(n)); refit(); }
    });
    addEventListener('resize', refit);
    document.body.appendChild(layer);
  }
  const hit = F.cards.get(F.popup);
  const scroll = layer.querySelector('.ppop-body')?.scrollTop ?? 0;
  const p = hit?.p;
  const title = p ? esc(p.name) : esc(F.popup);
  const body = !hit ? '<p class="ppop-wait">Loading…</p>' : !p ? `<p class="ppop-wait">No trainer named ${esc(F.popup)}.</p>` : cardBody(p);
  layer.innerHTML = `<section class="ppop panel" role="dialog" aria-label="${title}">
    <header class="ppop-bar"><h3>${title}</h3>${p ? `<a class="btn tiny" href="#/player/${encodeURIComponent(p.name)}" data-ppop-close>Full page</a>` : ''}<button class="btn tiny ppop-x" data-ppop-close aria-label="Close">✕</button></header>
    <div class="ppop-body">${body}</div></section>`;
  const b = layer.querySelector('.ppop-body');
  if (b) b.scrollTop = scroll;
  refit();
}

/** Page the showcase to fit the popup: it never scrolls (Cole 2026-10-27). */
function refit() { fitLists(layer?.querySelector<HTMLElement>('.ppop-body') ?? null); }

/** One showcase creature as a card: walking sprite, name, grade, elements, level and the colour-coded roll pills. */
function monCard(c: CreatureDTO, key: string, owner: string): string {
  setSnapshot(key, c, owner);
  const sp = SPECIES[c.species];
  return `<div class="ppx-mon${c.rolls.aura ? ' aura' : ''}" data-snap="${key}" tabindex="0">
    <div class="ppx-mon-pic">${creatureIcon(creatureArt(c), c.rolls.aura, esc(sp?.name ?? ''))}</div>
    <div class="ppx-mon-name"><b>${esc(cname(c))}</b>${creatureGrade(c, 'sm')}</div>
    <div class="ppx-mon-els">${(sp?.elements ?? []).map((e) => elChip(e)).join('')}<span class="ppx-mon-lv">Lv ${full(c.level)}</span></div>
    ${rollPills(c, { noLevel: true })}
  </div>`;
}

const tile = (value: string, label: string, cls = '') => `<div class="ppx-tile${cls ? ` ${cls}` : ''}"><b>${value}</b><span>${label}</span></div>`;

function cardBody(p: ProfileDTO): string {
  const need = trainerXpToNext(p.level);
  const frac = Math.max(0, Math.min(1, p.xp / need));
  const badge = p.badge ? BADGE[p.badge] : undefined;
  const st = p.stats;
  const head = `<div class="ppx-head">
    ${playerIcon({ outfit: p.outfit, comp: compOf(p), size: 'lg', name: p.name })}
    <div class="ppx-id">
      <div class="ppx-name">${p.guild ? tagChip(p.guild.tag) : ''}<b style="color:${p.nameColor ?? 'inherit'}">${esc(p.name)}</b>${badge ? `<span class="ppx-badge" title="${esc(badge.name)}">${esc(badge.glyph)}</span>` : ''}${p.online !== undefined ? `<i class="gdot${p.online ? '' : ' off'}" title="${p.online ? 'Online' : 'Offline'}"></i>` : ''}</div>
      ${p.title ? `<span class="ppx-title">${esc(p.title)}</span>` : ''}
      <div class="ppx-lv"><span>Lv <b>${full(p.level)}</b></span><span class="ppx-xp" title="${full(p.xp)} / ${full(need)} XP"><i style="width:${(frac * 100).toFixed(1)}%"></i></span><span class="ppx-pct">${Math.floor(frac * 100)}%</span></div>
      ${p.companion ? `<div class="ppx-comp">Companion <b data-snap="ppx-comp-${p.companion.id}" tabindex="0">${esc(cname(p.companion))}</b> <span>Lv ${full(p.companion.level)}</span></div>` : ''}
      <div class="ppx-acts">${actionsHtml(p, F.data)}</div>
    </div></div>`;
  if (p.companion) setSnapshot(`ppx-comp-${p.companion.id}`, p.companion, p.name);

  const arena = p.arena
    ? `${p.arena.ranked ? leagueBadge(p.arena.tierId as LeagueId) : tierBadge(p.arena.tierId as TierId, p.arena.tier)}<span class="ppx-ar"><b>${full(p.arena.ar)}</b> ${p.arena.ranked ? 'trophies' : 'AR'} · ${p.arena.wins}W / ${p.arena.losses}L</span>`
    : `<span class="ppx-none">${p.level < arenaOpensAt() ? `Arena opens at Lv ${arenaOpensAt()}` : 'No ranked match this season'}</span>`;
  const team = `<section class="ppx-sec"><div class="ppx-h"><h4>Arena team</h4><span class="ppx-note">order hidden</span></div>
    <div class="ppx-arena">${arena}</div>
    <div class="ar-team">${p.lineup.map((c) => teamFace(c)).join('') || '<span class="ppx-none">No creatures</span>'}</div></section>`;

  const show = `<section class="ppx-sec"><div class="ppx-h"><h4>Showcase</h4><span class="ppx-note">${p.showcase.length} / ${SOCIAL.showcaseMax}</span></div>
    ${p.showcase.length ? `<div class="ppx-mons" data-fit="ppx-mons">${p.showcase.map((c) => monCard(c, `ppx-${p.name}-${c.id}`, p.name)).join('')}</div>` : `<p class="ppx-none">${p.relation === 'self' ? '<a href="#/trainer/showcase" data-ppop-close>Choose creatures to show</a>' : 'Nothing on show.'}</p>`}</section>`;

  const stats = `<section class="ppx-sec"><div class="ppx-tiles">
    ${tile(`${full(st.species)}<small> / ${full(st.speciesOf)}</small>`, 'Bestiary', 't-acc')}${tile(num(st.catches), 'Caught')}${tile(num(st.kills), 'Defeated')}
    ${tile(st.ladderBest ? `Step ${full(st.ladderBest)}` : '—', `Boss Ladder${st.ladderWins ? ` · ${num(st.ladderWins)} wins` : ''}`, 't-bad')}${tile(full(st.trophies), st.trophies === 1 ? 'Trophy' : 'Trophies', 't-gold')}
    ${p.arena ? tile(full(p.arena.ar), `AR · ${esc(p.arena.tier)}`, 't-good') : tile('—', 'Arena')}
  </div></section>`;

  const coll = p.hidden?.rarities ? '' : `<section class="ppx-sec"><div class="ppx-h"><h4>Rarities</h4></div><div class="ppx-tiles">
    ${tile(full(st.aurasCaught), 'Auras caught', 't-gold')}${tile(full(st.aurasOwned), 'Auras owned', 't-gold')}
    ${tile(st.topGrade ? `${esc(st.topGrade)}<small> ×${full(st.topGradeN)}</small>` : '—', 'Top grade owned', 't-acc')}
  </div></section>`;

  const prog = `<section class="ppx-sec"><div class="ppx-h"><h4>Progress</h4></div><dl class="ppx-prog">
    <div><dt>Creatures owned</dt><dd>${full(st.owned)}</dd></div>
    ${p.hidden?.coins ? '' : `<div><dt>Coins</dt><dd class="ppx-coins">${full(st.coins)}</dd></div>`}
    <div><dt>Guild</dt><dd>${p.guild ? `${esc(p.guild.name)} ${tagChip(p.guild.tag)}` : '—'}</dd></div>
  </dl></section>`;
  // Showcase, arena team and the numbers take turns under tabs, at every size, so the card fits without scrolling.
  return head + phoneSections('ppx', [
    { key: 'show', title: 'Showcase', html: show },
    { key: 'team', title: 'Arena team', html: team },
    { key: 'stats', title: 'Stats', html: stats + coll + prog },
  ], undefined, true);
}

// ---------------- hover preview ----------------
function closeTip() {
  clearTimeout(hoverTimer);
  hoverName = ''; hoverEl = null;
  if (tip) tip.hidden = true;
}

function drawTip() {
  if (!hoverName || !hoverEl) return;
  const hit = F.cards.get(hoverName);
  if (!tip) { tip = document.createElement('div'); tip.className = 'ppop-tip panel'; tip.setAttribute('role', 'tooltip'); document.body.appendChild(tip); }
  const p = hit?.p;
  if (hit && !p) { tip.hidden = true; return; }
  tip.innerHTML = !p ? '<span class="ppop-wait">…</span>' : `<div class="ppt-head">${playerIcon({ outfit: p.outfit, comp: compOf(p), size: 'md' })}
      <div><div class="ppx-name">${p.guild ? tagChip(p.guild.tag) : ''}<b style="color:${p.nameColor ?? 'inherit'}">${esc(p.name)}</b></div>
      <div class="ppt-sub">Lv ${full(p.level)}${p.title ? ` · ${esc(p.title)}` : ''}${p.arena ? ` · ${esc(p.arena.tier)}` : ''}</div></div></div>
    ${p.showcase.length ? `<div class="ppt-mons">${p.showcase.map((c) => `<span class="ppt-mon${c.rolls.aura ? ' aura' : ''}">${creatureIcon(creatureArt(c), c.rolls.aura)}${creatureGrade(c, 'sm')}</span>`).join('')}</div>` : ''}
    <div class="ppt-stats"><span><b>${full(p.stats.species)}</b> species</span><span><b>${num(p.stats.catches)}</b> caught</span>${p.hidden?.rarities ? '' : `<span><b>${full(p.stats.aurasOwned)}</b> Auras</span>`}</div>`;
  tip.hidden = false;
  placeBeside(tip, hoverEl, { prefer: 'below', align: 'start' });
}

function startHover(el: HTMLElement) {
  const name = lc(nameOf(el));
  if (!name || name === hoverName) return;
  closeTip();
  hoverName = name; hoverEl = el;
  hoverTimer = window.setTimeout(() => {
    if (hoverName !== name) return;
    const hit = F.cards.get(name);
    if (!hit || Date.now() - hit.at > PEEK_FRESH_MS) askProfile(nameOf(el), true);
    drawTip();
  }, HOVER_DELAY_MS);
}

export function mountProfileCard() {
  bindCard((name) => { if (F.popup === name) draw(); if (hoverName === name) drawTip(); });
  document.addEventListener('click', (e) => {
    const a = (e.target as HTMLElement).closest?.('[data-player]') as HTMLElement | null;
    if (!a || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (a.closest('.ppop') && lc(nameOf(a)) === F.popup) { e.preventDefault(); return; }
    e.preventDefault();
    openProfileCard(nameOf(a));
  });
  document.addEventListener('pointerover', (e) => {
    if (e.pointerType !== 'mouse') return;
    const a = (e.target as HTMLElement).closest?.('[data-player]') as HTMLElement | null;
    // Not over a popup (the card itself, or the creature popup, which the preview would cover).
    if (a && !a.closest('.ppop, .peek')) startHover(a);
  });
  document.addEventListener('pointerout', (e) => {
    if (!hoverEl) return;
    const to = e.relatedTarget as Node | null;
    if (to && hoverEl.contains(to)) return;
    if ((e.target as HTMLElement).closest?.('[data-player]') === hoverEl) closeTip();
  });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && F.popup) { closeProfileCard(); } });
  window.addEventListener('hashchange', () => { closeTip(); if (F.popup) closeProfileCard(); });
  window.addEventListener('scroll', closeTip, true);
}

/** Redraw an open popup (the Friends view changed: friend buttons, the challenge wait). */
export function refreshProfileCard() { if (F.popup) draw(); }
